// TODO - проанализировать, зачем тут переменные snack_case
/* eslint-disable camelcase */
/* eslint-disable react/no-unused-prop-types */
/* forked from https://github.com/vitalybaev/autocomplete */
import { useEffect, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import type { FieldHookConfig } from 'formik';
import * as Highlighter from 'react-highlight-words';

import BaseInput from './StringInput';
import type { StringInputProps } from './StringInput';

const API_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address';

const getHighlightWords = (query = '') => {
  const wordsToPass = ['г', 'респ', 'ул', 'р-н', 'село', 'деревня', 'поселок', 'пр-д', 'пл', 'к', 'кв', 'обл', 'д'];

  return query?.replace && query.replace(',', '').split(' ').filter((word) => {
    return wordsToPass.indexOf(word) < 0;
  });
};

/**
 * Formik-инпут адреса с подсказками DaData (XHR без библиотек).
 * Formik-обёртка поверх StringInput (BaseInput).
 *
 * `onChange` здесь имеет семантику, отличную от StringInput: вызывается при ВЫБОРЕ
 * подсказки и получает объект подсказки DaData (не событие).
 */
export interface AddressSuggestionsInputProps extends Omit<StringInputProps, 'onChange'> {
  /** Кол-во подсказок в ответе DaData. @default 10 */
  count?: number;
  /**
   * Легаси-проп в lowerCase (не `autoComplete`): уходит в BaseInput
   * как `autoComplete={ autocomplete || 'off' }`. Оставлен 1:1 из .jsx-версии.
   */
  autocomplete?: string;
  /** Колбэк выбора подсказки — получает объект подсказки DaData (НЕ event). */
  onChange?: (suggestion: any) => void;
  /** Гранулярная нижняя граница DaData (см. BoundsType ниже). */
  fromBound?: string;
  /** Гранулярная верхняя граница DaData. */
  toBound?: string;
  /** Общий DaData-адрес для связи гранулярных полей (обязателен при fromBound/toBound). */
  address?: any;
}

const AddressSuggestionsInput = (props: AddressSuggestionsInputProps) => {
  // eslint-disable-next-line object-curly-newline
  const { autocomplete, onChange, ...rest } = props;

  const token = process.env.DADATA_TOKEN;
  const { setFieldValue, initialValues, values } = useFormikContext<any>();
  const [field] = useField(props as FieldHookConfig<any>);
  const [query, setQuery] = useState(field.value);
  const [inputQuery, setInputQuery] = useState(field.value);
  const [inputFocused, setInputFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  const [suggestionsVisible, setSuggestionsVisible] = useState(true);

  // ссылка на текущий XHR — у каждого инстанса своя
  // (раньше была module-level let request, и второй инстанс отменял запрос первого)
  const requestRef = useRef<XMLHttpRequest | null>(null);

  const fetchAddressSuggestions = (data: any, callback: (responseJson: any) => void) => {
    if (requestRef.current) {
      requestRef.current.abort();
    }

    const xhr = new XMLHttpRequest();
    requestRef.current = xhr;

    xhr.open('POST', API_URL);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Authorization', `Token ${ data.token }`);
    xhr.setRequestHeader('Content-Type', 'application/json');

    const requestPayload: any = {
      query: data.query,
      count: data.count ? data.count : 10,
    };

    if (data.fromBound && data.toBound) {
      // When using granular suggestion,
      // all dadata components have to receive address property
      // that contains shared address info.
      if (!data.address) {
        throw new Error('You have to pass address property with DaData address object to connect separate components');
      }

      requestPayload.from_bound = { value: data.fromBound };
      requestPayload.to_bound = { value: data.toBound };
      requestPayload.restrict_value = true;

      if (data.address.data) {
        // eslint-disable-next-line camelcase,object-curly-newline
        const { region_fias_id, city_fias_id, settlement_fias_id, street_fias_id } = data.address.data;

        requestPayload.locations = [{
          region_fias_id,
          city_fias_id,
          settlement_fias_id,
          street_fias_id,
        }];
      }
    }

    xhr.send(JSON.stringify(requestPayload));

    // замыкание над xhr (а не над requestRef.current) — гарантирует,
    // что callback видит «свой» XHR, даже если ref уже перезаписан новым запросом
    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) {
        return;
      }

      if (xhr.status === 200 && (typeof callback === 'function')) {
        callback(JSON.parse(xhr.response));
      }
    };
  };

  const selectSuggestion = (index: number) => {
    if (suggestions.length >= index - 1) {
      setQuery(suggestions[index]?.value);
      setInputQuery(suggestions[index]?.value);
      setSuggestionsVisible(false);

      if (onChange) {
        onChange(suggestions[index]);
      }
    }
  };

  const onKeyPress = (event: any) => {
    if (event.which === 40) {
      // Arrow down
      event.preventDefault();

      const newSuggestionIndex = suggestionIndex + 1;

      if ((suggestionIndex < suggestions.length) && suggestions[newSuggestionIndex]) {
        setInputQuery(suggestions[newSuggestionIndex].value);
        setSuggestionIndex(newSuggestionIndex);
      }
    } else if (event.which === 38) {
      // Arrow up
      event.preventDefault();

      if (suggestionIndex >= 0) {
        const newSuggestionIndex = suggestionIndex - 1;

        setInputQuery(newSuggestionIndex === -1 ? query : suggestions[newSuggestionIndex].value);
        setSuggestionIndex(newSuggestionIndex);
      }
    } else if (event.which === 13) {
      // Enter
      event.preventDefault();

      if (suggestionIndex >= 0) {
        selectSuggestion(suggestionIndex);
      }
    }
  };

  const onSuggestionClick = (event: any, index: number) => {
    event.stopPropagation();

    selectSuggestion(index);
  };

  useEffect(() => {
    setQuery(field.value);
    setInputQuery(field.value);
    setSuggestionsVisible(true);
  }, [field.value]);

  useEffect(() => {
    if (values[field.name] !== query) {
      setFieldValue(field.name, query);
    } else if (query && query !== initialValues[field.name]) {
      fetchAddressSuggestions({ ...props, query, token }, (responseJson) => {
        if (responseJson && responseJson.suggestions) {
          setSuggestions(responseJson.suggestions);
          setSuggestionIndex(-1);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <>
      <BaseInput
        { ...rest }
        autoComplete={ autocomplete || 'off' }
        onKeyPress={ onKeyPress }
        onKeyDown={ onKeyPress }
        onFocus={ () => setInputFocused(true) }
        onBlur={ () => setInputFocused(false) }
      />

      { inputFocused && suggestionsVisible && suggestions && suggestions.length > 0 && (
        <div className='autocomplete__suggestions'>
          <div className='autocomplete__suggestion-note'>
            Выберите вариант или продолжите ввод
          </div>

          {
            suggestions.map((suggestion, index) => {
              let suggestionClass = 'autocomplete__suggestion';
              if (index === suggestionIndex) {
                suggestionClass += ' autocomplete__suggestion--current';
              }

              return (
                <div
                  role='row'
                  tabIndex={ index }
                  key={ suggestion.value }
                  className={ suggestionClass }
                  onMouseDown={ (event) => onSuggestionClick(event, index) }
                >
                  <Highlighter
                    highlightClassName='autocomplete--highlighted'
                    autoEscape={ true }
                    searchWords={ getHighlightWords(inputQuery) }
                    textToHighlight={ suggestion.value }
                  />
                </div>
              );
            })
          }
        </div>
      ) }
    </>
  );
};

const BoundsType = ['region', 'area', 'city', 'settlement', 'street', 'house'];

export default AddressSuggestionsInput;
